import type { StatusTone } from "../../components/ui/StatusBadge";
import {
  Bed,
  BookOpen,
  Camera,
  Car,
  Clapperboard,
  Coffee,
  Dumbbell,
  Gift,
  Heart,
  Home,
  type LucideIcon,
  MapPin,
  Music,
  Percent,
  Plane,
  ShoppingBag,
  ShoppingCart,
  Sparkles,
  Star,
  Tag,
  Ticket,
  Tv,
  Utensils,
} from "lucide-react";

import {
  BenefitCtaMode,
  BenefitDiscountType,
  BenefitRedemptionMethod,
  BenefitStatus,
} from "../../models/benefits";

export const BENEFIT_MAX_IMAGES = 8;

export const BENEFIT_SLUG_REGEX = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

/** E.164 compacto: + y 8–15 dígitos (código de país incluido). */
export const BENEFIT_WHATSAPP_E164_REGEX = /^\+[1-9]\d{7,14}$/;

export const BENEFIT_WHATSAPP_FORMAT_MESSAGE =
  "Incluí el código de país (ej: +54 11 2345 6789)";

export const sanitizeBenefitWhatsappNumberInput = (value: string): string =>
  value.replace(/[^\d+\s\-()]/g, "");

export const normalizeBenefitWhatsappNumber = (value: string): string =>
  value.trim().replace(/[\s\-()]/g, "");

export const isValidBenefitWhatsappNumber = (value: string): boolean => {
  if (!value.trim()) return true;
  return BENEFIT_WHATSAPP_E164_REGEX.test(normalizeBenefitWhatsappNumber(value));
};

export const benefitStatusOptions: { label: string; value: BenefitStatus }[] = [
  { label: "Activo", value: "active" },
  { label: "Inactivo", value: "inactive" },
  { label: "Borrador", value: "draft" },
];

export const getBenefitStatusLabel = (status?: string | null): string =>
  benefitStatusOptions.find((o) => o.value === status)?.label ?? status ?? "-";

export const getBenefitStatusSeverity = (
  status?: string | null
): "success" | "danger" | "warning" | undefined => {
  switch (status) {
    case "active":
      return "success";
    case "inactive":
      return "danger";
    case "draft":
      return "warning";
    default:
      return undefined;
  }
};

/**
 * Tono del §3.2 para el estado de un beneficio.
 *
 * Ojo con `draft`: getBenefitStatusSeverity lo mapeaba a "warning", pero el
 * §3.2 asigna info/bg-light explicitamente a "badge neutro / borrador", y
 * warning/alt al estado "Pendiente". Un borrador no es una advertencia.
 */
export const getBenefitStatusTone = (status?: string | null): StatusTone => {
  switch (status) {
    case "active":
      return "success";
    case "inactive":
      return "error";
    case "draft":
      return "info";
    case "pending":
      return "pending";
    default:
      return "info";
  }
};

export const benefitDiscountTypeOptions: {
  label: string;
  value: BenefitDiscountType;
}[] = [
  { label: "Porcentaje", value: "percentage" },
  { label: "2x1", value: "two_for_one" },
  { label: "Ítem gratis", value: "free_item" },
  { label: "Cuotas", value: "installments" },
  { label: "Combo", value: "combo" },
  { label: "Otro", value: "other" },
];

export const getBenefitDiscountTypeLabel = (value?: string | null): string =>
  benefitDiscountTypeOptions.find((o) => o.value === value)?.label ?? value ?? "-";

export const benefitCtaModeOptions: {
  label: string;
  value: BenefitCtaMode;
  /** Qué ve el miembro en la app con este modo (tarjetas del formulario). */
  description: string;
  icon: string;
}[] = [
  {
    label: "Canje",
    value: "redemption",
    description: "Botón en la app para usar el beneficio",
    icon: "pi pi-ticket",
  },
  {
    label: "Texto informativo",
    value: "info_text",
    description: "Instrucciones en texto, sin botón de canje",
    icon: "pi pi-info-circle",
  },
];

/**
 * Modo de CTA efectivo de un beneficio. `cta_mode` admite NULL (beneficios
 * anteriores al campo): en ese caso la app lo deduce de los datos (con métodos
 * de canje → canje; con texto informativo → texto informativo) y el BO usa la
 * misma regla. Antes el formulario arrancaba siempre en "Canje": un beneficio
 * viejo de texto informativo se abría con el modo equivocado y no se podía
 * guardar porque pedía métodos de canje.
 */
export const resolveBenefitCtaMode = (
  benefit?: {
    ctaMode?: BenefitCtaMode | null;
    redemptionMethods?: BenefitRedemptionMethod[] | null;
    ctaInfoText?: string | null;
  } | null
): BenefitCtaMode | null => {
  if (!benefit) return null;
  if (benefit.ctaMode) return benefit.ctaMode;
  if (benefit.redemptionMethods?.length) return "redemption";
  if (benefit.ctaInfoText?.trim()) return "info_text";
  return null;
};

export const benefitRedemptionMethodOptions: {
  label: string;
  value: BenefitRedemptionMethod;
}[] = [
  { label: "Credencial visual", value: "visual_credential" },
  { label: "Código promocional", value: "promo_code" },
  { label: "QR dinámico", value: "qr_dynamic" },
  { label: "PIN", value: "pin" },
  { label: "Link de activación", value: "activation_link" },
];

export const getBenefitRedemptionMethodLabel = (value?: string | null): string =>
  benefitRedemptionMethodOptions.find((o) => o.value === value)?.label ?? value ?? "-";

/** Días de validez (ints ISO 8601: 1=Lunes .. 7=Domingo). */
export const benefitValidDayOptions: { label: string; value: number }[] = [
  { label: "Lunes", value: 1 },
  { label: "Martes", value: 2 },
  { label: "Miércoles", value: 3 },
  { label: "Jueves", value: 4 },
  { label: "Viernes", value: 5 },
  { label: "Sábado", value: 6 },
  { label: "Domingo", value: 7 },
];

/**
 * Íconos Lucide sugeridos para categorías de beneficios (icon_name).
 * El valor canónico guardado es el nombre kebab-case de Lucide.
 */
export const benefitCategoryIconOptions: { label: string; value: string }[] = [
  { label: "Deporte / Gimnasios", value: "dumbbell" },
  { label: "Gastronomía / Bares", value: "utensils" },
  { label: "Salud y Estética", value: "sparkles" },
  { label: "Librería / Educación", value: "book-open" },
  { label: "Estudios (Foto y Video)", value: "camera" },
  { label: "Turismo y Transporte", value: "car" },
  { label: "Alojamiento", value: "bed" },
  { label: "Entretenimiento / Streaming", value: "clapperboard" },
  { label: "Tecnología / Electro", value: "tv" },
  { label: "Compras", value: "shopping-bag" },
  { label: "Supermercado", value: "shopping-cart" },
  { label: "Bienestar", value: "heart" },
  { label: "Eventos", value: "ticket" },
  { label: "Viajes", value: "plane" },
  { label: "Hogar", value: "home" },
  { label: "Café", value: "coffee" },
  { label: "Regalos", value: "gift" },
  { label: "Descuentos", value: "percent" },
  { label: "Destacados", value: "star" },
  { label: "Música", value: "music" },
  { label: "Local", value: "map-pin" },
  { label: "Otro", value: "tag" },
];

/**
 * Mapea el nombre kebab-case de Lucide guardado en icon_name al componente de
 * lucide-react. Los valores legacy "pi pi-*" (o cualquier desconocido) caen en
 * Tag como fallback para no romper categorías existentes.
 */
const lucideCategoryIconMap: Record<string, LucideIcon> = {
  dumbbell: Dumbbell,
  utensils: Utensils,
  sparkles: Sparkles,
  "book-open": BookOpen,
  camera: Camera,
  car: Car,
  bed: Bed,
  clapperboard: Clapperboard,
  tv: Tv,
  "shopping-bag": ShoppingBag,
  "shopping-cart": ShoppingCart,
  heart: Heart,
  ticket: Ticket,
  plane: Plane,
  home: Home,
  coffee: Coffee,
  gift: Gift,
  percent: Percent,
  star: Star,
  music: Music,
  "map-pin": MapPin,
  tag: Tag,
};

export const getBenefitCategoryLucideIcon = (
  iconName?: string | null
): LucideIcon => (iconName && lucideCategoryIconMap[iconName]) || Tag;
